import { useState, useRef, useEffect, useCallback } from 'react'
import { authFetch } from '../../lib/api'

type Tool = 'pen' | 'rect' | 'text' | 'arrow' | 'eraser'
interface Shape {
  type: Tool
  points?: { x: number; y: number }[]
  x?: number; y?: number; w?: number; h?: number
  text?: string
  color: string
}

export function CanvasPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [tool, setTool] = useState<Tool>('pen')
  const [color, setColor] = useState('#7c6ef5')
  const [shapes, setShapes] = useState<Shape[]>([])
  const [drawing, setDrawing] = useState(false)
  const [current, setCurrent] = useState<Shape | null>(null)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [generating, setGenerating] = useState(false)
  const [generatedCode, setGeneratedCode] = useState('')

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'
    for (let x = 0; x < canvas.width; x += 20) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke() }
    for (let y = 0; y < canvas.height; y += 20) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke() }
    // Shapes
    const all = current ? [...shapes, current] : shapes
    for (const s of all) {
      ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = 2
      if (s.type === 'pen' && s.points?.length) {
        ctx.beginPath(); ctx.moveTo(s.points[0].x, s.points[0].y)
        for (const p of s.points.slice(1)) ctx.lineTo(p.x, p.y)
        ctx.stroke()
      } else if (s.type === 'eraser' && s.points?.length) {
        ctx.strokeStyle = '#0c0e18'; ctx.lineWidth = 12
        ctx.beginPath(); ctx.moveTo(s.points[0].x, s.points[0].y)
        for (const p of s.points.slice(1)) ctx.lineTo(p.x, p.y)
        ctx.stroke()
      } else if (s.type === 'rect' && s.w !== undefined) {
        ctx.strokeRect(s.x!, s.y!, s.w, s.h!)
      } else if (s.type === 'arrow' && s.points?.length === 2) {
        const [a, b] = s.points
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
        const angle = Math.atan2(b.y - a.y, b.x - a.x)
        ctx.beginPath(); ctx.moveTo(b.x, b.y)
        ctx.lineTo(b.x - 12 * Math.cos(angle - 0.4), b.y - 12 * Math.sin(angle - 0.4))
        ctx.lineTo(b.x - 12 * Math.cos(angle + 0.4), b.y - 12 * Math.sin(angle + 0.4))
        ctx.closePath(); ctx.fill()
      } else if (s.type === 'text' && s.text) {
        ctx.font = '14px var(--font-mono)'; ctx.fillText(s.text, s.x!, s.y!)
      }
    }
  }, [shapes, current])

  useEffect(() => { redraw() }, [redraw])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; redraw() }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [redraw])

  const getPos = (e: React.MouseEvent) => {
    const r = canvasRef.current!.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  const onDown = (e: React.MouseEvent) => {
    const pos = getPos(e)
    setDrawing(true); setStartPos(pos)
    if (tool === 'pen' || tool === 'eraser') setCurrent({ type: tool, points: [pos], color })
    else if (tool === 'rect') setCurrent({ type: 'rect', x: pos.x, y: pos.y, w: 0, h: 0, color })
    else if (tool === 'arrow') setCurrent({ type: 'arrow', points: [pos, pos], color })
    else if (tool === 'text') {
      const text = prompt('Enter text:')
      if (text) setShapes(s => [...s, { type: 'text', x: pos.x, y: pos.y, text, color }])
    }
  }

  const onMove = (e: React.MouseEvent) => {
    if (!drawing || !current) return
    const pos = getPos(e)
    if (current.type === 'pen' || current.type === 'eraser') setCurrent({ ...current, points: [...current.points!, pos] })
    else if (current.type === 'rect') setCurrent({ ...current, w: pos.x - startPos.x, h: pos.y - startPos.y })
    else if (current.type === 'arrow') setCurrent({ ...current, points: [startPos, pos] })
  }

  const onUp = () => {
    if (current) setShapes(s => [...s, current])
    setCurrent(null); setDrawing(false)
  }

  const handleGenerateCode = async () => {
    const canvas = canvasRef.current
    if (!canvas) return
    setGenerating(true)
    const dataUrl = canvas.toDataURL('image/png')
    try {
      const r = await authFetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Look at this wireframe/mockup image and generate clean React + Tailwind code that implements this UI. Only output the code, no explanation.\n\n[Image data: ${dataUrl.slice(0, 200)}... (canvas snapshot)]` }],
          model: 'gemini-2.5-flash',
        }),
      })
      if (r.ok) {
        const reader = r.body!.getReader()
        const decoder = new TextDecoder()
        let code = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          code += decoder.decode(value, { stream: true })
        }
        setGeneratedCode(code)
      }
    } catch {}
    setGenerating(false)
  }

  const tools: { id: Tool; icon: string; label: string }[] = [
    { id: 'pen', icon: '✏️', label: 'Draw' },
    { id: 'rect', icon: '▢', label: 'Rectangle' },
    { id: 'arrow', icon: '→', label: 'Arrow' },
    { id: 'text', icon: 'T', label: 'Text' },
    { id: 'eraser', icon: '⌫', label: 'Eraser' },
  ]

  const btn: React.CSSProperties = { padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }

  return (
    <div style={{ height: '100%', width: '100%', flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-1)' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {tools.map(t => (
          <button key={t.id} onClick={() => setTool(t.id)} style={{ ...btn, background: tool === t.id ? 'var(--accent)' : 'var(--bg-3)', color: tool === t.id ? '#fff' : 'var(--text-1)' }} title={t.label}>
            {t.icon}
          </button>
        ))}
        <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 24, height: 24, border: 'none', cursor: 'pointer' }} />
        <div style={{ flex: 1 }} />
        <button style={btn} onClick={() => { setShapes([]); setGeneratedCode('') }}>Clear</button>
        <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={handleGenerateCode} disabled={generating}>
          {generating ? '⏳ Generating...' : '🤖 Generate Code'}
        </button>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', cursor: tool === 'text' ? 'text' : 'crosshair' }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        />
      </div>

      {/* Generated code output */}
      {generatedCode && (
        <div style={{ maxHeight: 200, overflow: 'auto', borderTop: '1px solid var(--border)', padding: 10, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', whiteSpace: 'pre-wrap', background: 'var(--bg-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong style={{ color: 'var(--accent)' }}>Generated Code</strong>
            <button style={btn} onClick={() => navigator.clipboard.writeText(generatedCode)}>📋 Copy</button>
          </div>
          {generatedCode}
        </div>
      )}
    </div>
  )
}
